import { useEffect, useState, useCallback } from "react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Globe, ShieldCheck } from "lucide-react";
import { DepthLabel } from "@/components/Shell";
import { countryName } from "@/lib/countries";

// P6 Compliance Scope: master toggle + Q1 target-market + Q2 jurisdiction picker.
export default function ComplianceScope({ projectId, onChange }) {
  const [scope, setScope] = useState(null);
  const [opts, setOpts] = useState([]);
  const [q1, setQ1] = useState(null);
  const [custom, setCustom] = useState("");
  const [saving, setSaving] = useState(false);
  const [q1sel, setQ1sel] = useState(null);

  const load = useCallback(async () => {
    const [s, j] = await Promise.all([
      api.get(`/projects/${projectId}/scope`),
      api.get("/scope/jurisdictions"),
    ]);
    setScope(s.data); setOpts(j.data.jurisdictions);
    setQ1(j.data.target_market_question);
  }, [projectId]);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const save = async (track, codes, target_market, custom_country) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/projects/${projectId}/scope`, { track, jurisdictions: codes, target_market, custom_country });
      setScope(s => ({ ...data.scope, changelog: [...(s?.changelog || [])], payment_suggestions: data.scope.jurisdictions.length ? undefined : s?.payment_suggestions }));
      await load();
      onChange?.(data);
      if (data.requires_new_quote) toast.warning("Scope changed after a build — a new fixed quote is required.");
      else toast.success("Compliance scope updated");
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  if (!scope) return null;
  const on = scope.track === "uk_eu_default";
  const codes = scope.codes || [];

  const toggleMaster = () => {
    if (on) save("custom_scope", codes.filter(c => !["UK", "EU"].includes(c)));
    else save("uk_eu_default", []);
  };
  const toggleJur = (code) => {
    const has = codes.includes(code);
    save("custom_scope", has ? codes.filter(c => c !== code) : [...codes, code]);
  };
  const answerQ1 = (key) => {
    setQ1sel(key);
    if (key === "a") save("uk_eu_default", [], "a");
    else if (key === "c") save("custom_scope", ["GLOBAL_DIASPORA"], "c");
    else save("custom_scope", codes, key); // b or d -> open picker / no selection
  };

  return (
    <div className="k-container" data-testid="compliance-scope" style={{ padding: 22, marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <ShieldCheck size={18} color="var(--accent)" />
          <h3 className="k-heading" style={{ fontSize: 17, margin: 0 }}>Target market & compliance scope</h3>
        </div>
        <button className={`k-badge ${on ? "k-badge-accent" : ""}`} data-testid="master-toggle"
          disabled={saving} style={{ cursor: "pointer" }} onClick={toggleMaster}>
          UK/EU compliance architecture: {on ? "ON" : "OFF"}
        </button>
      </div>

      {/* Q1 target-market question */}
      {q1 && (
        <div style={{ marginBottom: 14 }} data-testid="q1-target-market">
          <div className="k-heading" style={{ fontSize: 14, fontWeight: 600 }}>{q1.q}</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
            {q1.options.map(o => (
              <button key={o.key} data-testid={`q1-${o.key}`}
                className={`k-control ${(q1sel ?? scope.target_market) === o.key ? "k-badge-accent" : ""}`}
                style={{ textAlign: "left", cursor: "pointer", fontSize: 13 }}
                onClick={() => answerQ1(o.key)}>{o.label}</button>
            ))}
          </div>
        </div>
      )}

      {/* Q1(d) empty-scope banner */}
      {scope.is_empty && (
        <p className="k-tag-warn" data-testid="no-market-banner" style={{ fontSize: 13, padding: 10, borderRadius: 8 }}>
          No target market chosen — legal and compliance modules are off until you pick one.
          The Coach, quotes, export and universal checks still work.
        </p>
      )}

      {/* Q2 jurisdiction picker (only when master toggle OFF) */}
      {!on && (
        <div data-testid="jurisdiction-picker">
          <div className="k-muted" style={{ fontSize: 12, marginBottom: 8 }}>
            Choose exactly the jurisdictions you want. UK and EU are independent — picking EU never pulls in UK rules.
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {opts.map(o => (
              <button key={o.code} data-testid={`jur-${o.code}`}
                className={`k-badge ${codes.includes(o.code) ? "k-badge-accent" : ""}`}
                style={{ cursor: "pointer", display: "flex", gap: 6 }} onClick={() => toggleJur(o.code)}>
                {countryName(o.name)} <DepthLabel depth={o.depth} />
              </button>
            ))}
          </div>
          <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
            <input className="k-control" data-testid="custom-country" placeholder="Type another country (stored, BASELINE)"
              value={custom} onChange={e => setCustom(e.target.value)} style={{ fontSize: 13, flex: 1 }} />
            <button className="k-btn k-btn-ghost" data-testid="add-custom-country" disabled={!custom.trim()}
              onClick={() => { save("custom_scope", codes, "b", custom.trim()); setCustom(""); }}>Add</button>
          </div>
        </div>
      )}

      {/* current scope summary */}
      <div className="k-control" data-testid="scope-summary" style={{ marginTop: 12, fontSize: 13 }}>
        <div className="k-heading" style={{ fontSize: 13, fontWeight: 600 }}>{scope.scope_statement}</div>
        {scope.jurisdictions?.length > 0 && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
            {scope.jurisdictions.map(j => (
              <span key={j.code} className="k-badge" data-testid={`scope-active-${j.code}`}>{j.name} <DepthLabel depth={j.depth} /></span>
            ))}
          </div>
        )}
        {scope.payment_suggestions?.length > 0 && (
          <div className="k-muted" style={{ fontSize: 12, marginTop: 8 }}>
            Suggested payments (in order): {scope.payment_suggestions.join(", ")}
          </div>
        )}
        <p className="k-muted" style={{ fontSize: 11, marginTop: 8 }}>
          Hosting region is a separate choice from compliance scope and never implies it.
        </p>
      </div>
    </div>
  );
}
